import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import UserSearchInput from './UserSearchInput';
import MessageComposer from './MessageComposer';
import { createMessage } from '../../services/messagingService';
import './NewMessageModal.css';

function NewMessageModal({ show, onHide, onMessageSent }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const handleUserSelect = (user) => {
    if (!selectedUsers.find(u => u.userId === user.userId)) {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleUserRemove = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.userId !== userId));
  };

  const handleSendMessage = async (content) => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    if (!content.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const recipientIds = selectedUsers.map(u => u.userId);
      const newThread = await createMessage({
        recipients: recipientIds,
        content: content.trim()
      });

      // Reset form
      setSelectedUsers([]);
      
      // Notify parent
      if (onMessageSent) {
        onMessageSent(newThread);
      }
      
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to send message');
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setSelectedUsers([]);
      setError(null);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" className="new-message-modal">
      <Modal.Header closeButton>
        <Modal.Title>New Message</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>To:</Form.Label>
          
          {/* Selected users chips */}
          {selectedUsers.length > 0 && (
            <div className="selected-users mb-2">
              {selectedUsers.map(user => (
                <div key={user.userId} className="user-chip">
                  <span>{user.displayName}</span>
                  <button
                    type="button"
                    className="remove-user-btn"
                    onClick={() => handleUserRemove(user.userId)}
                    disabled={sending}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* User search input */}
          <UserSearchInput
            onUserSelect={handleUserSelect}
            excludeUserIds={selectedUsers.map(u => u.userId)}
            disabled={sending}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Message:</Form.Label>
          <MessageComposer
            onSend={handleSendMessage}
            disabled={sending || selectedUsers.length === 0}
            placeholder="Type your message..."
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={sending}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default NewMessageModal;
